import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  BookOpen,
  Calendar,
  Edit2,
  ExternalLink,
  FileText,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import PageHeader from './layout/PageHeader';
import { ConfirmModal } from './ConfirmModal';
import ModalPortal from './ModalPortal';
import {
  PLAYBOOK_MAX_BYTES,
  formatPlaybookSize,
  playbookService,
  type Playbook,
} from '../services/playbookService';

const ALLOWED_SPORTS = new Set(['Football', 'FlagFootball']);

const PlaybookLibrary: React.FC = () => {
  const { currentTeam, fetchAllTeams } = useTeamStore();
  const [teamsLoading, setTeamsLoading] = useState(true);

  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState<Playbook | null>(null);
  const [deletingPlaybook, setDeletingPlaybook] = useState<Playbook | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setTeamsLoading(true);
      await fetchAllTeams();
      if (!cancelled) setTeamsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAllTeams]);

  const loadPlaybooks = useCallback(async () => {
    if (!currentTeam?.id) return;
    setLoading(true);
    try {
      const rows = await playbookService.list(currentTeam.id);
      setPlaybooks(rows);
    } catch (err) {
      console.error('Failed to load playbooks:', err);
      toast.error('Failed to load playbooks.');
    } finally {
      setLoading(false);
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (teamsLoading || !currentTeam?.id) return;
    void loadPlaybooks();
  }, [teamsLoading, currentTeam?.id, loadPlaybooks]);

  const existingTags = useMemo(
    () => playbooks.map((pb) => pb.tag.trim().toLowerCase()),
    [playbooks],
  );

  const handleView = useCallback(async (pb: Playbook) => {
    try {
      const url = await playbookService.getSignedUrl(pb);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to open playbook:', err);
      toast.error('Failed to open playbook.');
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deletingPlaybook) return;
    setIsDeleting(true);
    try {
      await playbookService.delete(deletingPlaybook);
      toast.success('Playbook deleted.');
      setDeletingPlaybook(null);
      await loadPlaybooks();
    } catch (err) {
      console.error('Failed to delete playbook:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete playbook.';
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingPlaybook, loadPlaybooks]);

  if (teamsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="container mx-auto px-4 py-4 text-center">
        <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-700 mb-2">No team is selected yet.</p>
        <p className="text-sm text-gray-500 mb-4">
          Open Home and pick your team first, then return to Playbooks.
        </p>
        <Link to="/dashboard" className="btn-primary inline-block">
          Go to Home
        </Link>
      </div>
    );
  }

  if (!ALLOWED_SPORTS.has(currentTeam.sport as string)) {
    return (
      <div className="container mx-auto px-4 py-4">
        <PageHeader title="Playbooks" icon={<BookOpen className="h-6 w-6" />} />
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">
            Playbooks are only available for Football and Flag Football teams.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <PageHeader
        title="Playbooks"
        icon={<BookOpen className="h-6 w-6" />}
        actions={
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center gap-2"
            aria-label="Add playbook"
          >
            <Plus size={16} />
            Add Playbook
          </button>
        }
      />

      <p className="text-sm text-stone-600 mb-4">
        Upload PDF playbooks (up to 10MB each) and tag them however you like —
        e.g. <em>Offense 2025</em>, <em>Week 1 Install</em>, or{' '}
        <em>Trick Plays</em>.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
        </div>
      ) : playbooks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-700 mb-1 font-medium">No playbooks yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Upload your first playbook to get started.
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary inline-flex items-center gap-2"
            aria-label="Upload your first playbook"
          >
            <Upload size={16} />
            Upload Playbook
          </button>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {playbooks.map((pb) => (
            <li
              key={pb.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3
                    className="text-lg font-semibold text-gray-900 truncate"
                    title={pb.tag}
                  >
                    {pb.tag}
                  </h3>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                    <FileText size={14} />
                    <span className="truncate" title={pb.filename}>
                      {pb.filename}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(pb.uploaded_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <span>{formatPlaybookSize(pb.file_size)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleView(pb)}
                    className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-sm"
                    aria-label={`View playbook ${pb.tag}`}
                  >
                    <ExternalLink size={14} />
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingPlaybook(pb)}
                    className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 text-sm"
                    aria-label={`Edit tag for ${pb.tag}`}
                  >
                    <Edit2 size={14} />
                    Edit Tag
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingPlaybook(pb)}
                    className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"
                    aria-label={`Delete playbook ${pb.tag}`}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showUploadModal && currentTeam && (
        <UploadPlaybookModal
          teamId={currentTeam.id}
          existingTags={existingTags}
          onClose={() => setShowUploadModal(false)}
          onUploaded={async () => {
            setShowUploadModal(false);
            await loadPlaybooks();
          }}
        />
      )}

      {editingPlaybook && (
        <EditTagModal
          playbook={editingPlaybook}
          onClose={() => setEditingPlaybook(null)}
          onSaved={async () => {
            setEditingPlaybook(null);
            await loadPlaybooks();
          }}
        />
      )}

      {deletingPlaybook && (
        <ConfirmModal
          isOpen={!!deletingPlaybook}
          onClose={() => (isDeleting ? undefined : setDeletingPlaybook(null))}
          onConfirm={handleDelete}
          title={`Delete "${deletingPlaybook.tag}"?`}
          message="This will permanently remove the playbook PDF and cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          isConfirming={isDeleting}
          confirmAriaLabel="Confirm delete playbook"
        />
      )}
    </div>
  );
};

interface UploadPlaybookModalProps {
  teamId: string;
  existingTags: string[];
  onClose: () => void;
  onUploaded: () => void | Promise<void>;
}

const UploadPlaybookModal: React.FC<UploadPlaybookModalProps> = ({
  teamId,
  existingTags,
  onClose,
  onUploaded,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [tag, setTag] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hasConfirmedDuplicateTag, setHasConfirmedDuplicateTag] = useState(false);

  const duplicateWarning =
    tag.trim() && existingTags.includes(tag.trim().toLowerCase());

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const chosen = e.target.files?.[0] ?? null;
    if (chosen) {
      const isPdf =
        chosen.type === 'application/pdf' || /\.pdf$/i.test(chosen.name);
      if (!isPdf) {
        setError('Only PDF files are supported.');
        setFile(null);
        return;
      }
      if (chosen.size > PLAYBOOK_MAX_BYTES) {
        setError(
          `That file is ${formatPlaybookSize(
            chosen.size,
          )}. Playbooks must be 10MB or smaller.`,
        );
        setFile(null);
        return;
      }
    }
    setFile(chosen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!file) {
      setError('Please choose a PDF file.');
      return;
    }
    if (!tag.trim()) {
      setError('Please enter a tag for this playbook.');
      return;
    }
    if (duplicateWarning && !hasConfirmedDuplicateTag) {
      setHasConfirmedDuplicateTag(true);
      return;
    }

    setIsUploading(true);
    try {
      await playbookService.upload({ teamId, tag: tag.trim(), file });
      toast.success('Playbook uploaded.');
      await onUploaded();
    } catch (err) {
      console.error('Failed to upload playbook:', err);
      const msg = err instanceof Error ? err.message : 'Failed to upload playbook.';
      setError(msg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ModalPortal onClose={isUploading ? () => undefined : onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Add Playbook</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close upload playbook"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label
              htmlFor="playbook-file"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              PDF file
            </label>
            <input
              ref={fileInputRef}
              id="playbook-file"
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleFileChange}
              disabled={isUploading}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
              aria-label="Playbook PDF file"
            />
            <p className="mt-1 text-xs text-gray-500">PDF only. Up to 10MB.</p>
            {file && (
              <p className="mt-1 text-xs text-gray-600">
                Selected: <strong>{file.name}</strong> ({formatPlaybookSize(file.size)})
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="playbook-tag"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tag <span className="text-red-500">*</span>
            </label>
            <input
              id="playbook-tag"
              type="text"
              value={tag}
              onChange={(e) => {
                setTag(e.target.value);
                setHasConfirmedDuplicateTag(false);
              }}
              required
              disabled={isUploading}
              placeholder="e.g. Offense 2025, Week 1 Install, Trick Plays"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Playbook tag"
              maxLength={120}
            />
            {duplicateWarning && (
              <p className="mt-1 text-xs text-yellow-700">
                You already have a playbook tagged{' '}
                <strong>&quot;{tag.trim()}&quot;</strong>.{' '}
                {hasConfirmedDuplicateTag
                  ? 'Click upload again to confirm.'
                  : 'You can keep it or change the tag.'}
              </p>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !file || !tag.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              aria-label="Upload playbook"
            >
              {isUploading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalPortal>
  );
};

interface EditTagModalProps {
  playbook: Playbook;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const EditTagModal: React.FC<EditTagModalProps> = ({ playbook, onClose, onSaved }) => {
  const [tag, setTag] = useState(playbook.tag);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tag.trim()) {
      setError('Tag is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await playbookService.updateTag(playbook.id, tag.trim());
      toast.success('Tag updated.');
      await onSaved();
    } catch (err) {
      console.error('Failed to update tag:', err);
      const msg = err instanceof Error ? err.message : 'Failed to update tag.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={saving ? () => undefined : onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Edit Playbook Tag</h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close edit tag"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSave} className="p-4 space-y-4">
          <div>
            <label
              htmlFor="edit-playbook-tag"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tag <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-playbook-tag"
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              required
              disabled={saving}
              maxLength={120}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              aria-label="Playbook tag"
            />
          </div>
          {error && (
            <div
              role="alert"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2"
            >
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !tag.trim() || tag.trim() === playbook.tag}
              className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Save playbook tag"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </ModalPortal>
  );
};

export default PlaybookLibrary;
